import { useCallback, useEffect, useMemo, useState } from "react";

import {
  forgetVisit,
  readVisits,
  rememberVisit,
  savedRooms,
  type RoomVisit,
  type SavedRoom,
} from "@/engine/state/savedRooms";
import {
  listAccountRooms,
  releaseRoom,
  type AccountRoom,
} from "@/adapters/supabase/roomRecord";
import { peekRoom } from "@/adapters/supabase/roomPeek";
import type { RoomPeek } from "@/engine/state/roomPeek";

/**
 * How many of the listed rooms are looked at.
 *
 * Every one that is costs a channel held open for as long as the list is on
 * screen, so this is the top of the list rather than the whole of it: the rooms
 * somebody is most likely to go back to are the ones worth saying how busy they
 * are, and a rail that opened twenty channels to fill in twenty numbers would
 * be paying the room's price to decorate a list.
 */
export const ROOMS_LOOKED_AT = 6;

/** Where the visits are kept, when there is anywhere to keep them. */
function visitStore() {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // A browser with storage switched off still runs the application; it just
    // does not remember where it has been.
    return null;
  }
}

/** Remembers that this browser was in a room. Safe to call anywhere. */
export function rememberRoomVisit(code: string, at = Date.now()) {
  rememberVisit(visitStore(), code, at);
}

/**
 * The rooms this visitor can go back to, and how busy the first few are.
 *
 * Two sources joined: the rooms the account created, which it keeps, and the
 * rooms this browser has been in. Nothing is listed for somebody who is not
 * signed in — the account is what makes the first half durable, and a list of
 * one browser's history shown to a stranger on a shared machine is not an offer
 * anybody asked for.
 *
 * The whole list is returned and only the first few of it are looked at — and
 * the room this tab is standing in is not one of those, for the reason under
 * `looked`.
 */
export function useSavedRooms(
  accountId: string | null,
  /** The room this tab is standing in, when it is standing in one. */
  hereCode?: string,
): {
  rooms: SavedRoom[];
  /** How many are standing in each, for the rooms that answered. */
  counts: Record<string, number>;
  /**
   * Gives up a room for good. True where it is gone; false where the database
   * refused, which is where the list must keep showing it.
   */
  forget: (code: string) => Promise<boolean>;
} {
  const [owned, setOwned] = useState<AccountRoom[]>([]);
  const [visits, setVisits] = useState<RoomVisit[]>(() =>
    accountId ? readVisits(visitStore()) : [],
  );
  // Whose list is on screen. Storage is read while rendering rather than after
  // it, because a read that waits for an effect shows the previous account's
  // rooms for a frame first — and the account is what decides whether this
  // browser's history may be shown at all. Signing out empties both halves;
  // signing in as somebody else keeps the rooms already fetched until the
  // request below replaces them, which is what the effect did before.
  const [shownFor, setShownFor] = useState(accountId);
  if (shownFor !== accountId) {
    setShownFor(accountId);
    if (accountId) {
      setVisits(readVisits(visitStore()));
    } else {
      setOwned([]);
      setVisits([]);
    }
  }

  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    void listAccountRooms({ ownerId: accountId }).then((rooms) => {
      if (alive) setOwned(rooms);
    });
    return () => {
      alive = false;
    };
  }, [accountId]);

  const rooms = useMemo(() => savedRooms(owned, visits), [owned, visits]);
  const looked = useMemo(
    () =>
      rooms
        // The room this tab is in is not one of the few that are looked at. It
        // is not on the list to begin with — you cannot go back to where you
        // are — and the tab is already on its channel, so the look would be
        // spent on the one room nobody needs it for.
        .filter((room) => room.code !== hereCode)
        .slice(0, ROOMS_LOOKED_AT)
        .map((room) => room.code)
        .join(" "),
    [rooms, hereCode],
  );
  const counts = useRoomCounts(looked);

  // The row goes only once the row is gone. A refusal — somebody else's room,
  // a read-only moment — leaves the list exactly as it was, because a list that
  // hides a room the database still holds is a list that lies.
  const forget = useCallback(async (code: string) => {
    const outcome = await releaseRoom({ code });
    if (outcome !== "released") return false;
    setOwned((was) => was.filter((room) => room.code !== code));
    setVisits(forgetVisit(visitStore(), code));
    return true;
  }, []);

  return { rooms, counts, forget };
}

/**
 * How many are standing in each of these rooms, while the list is on screen.
 *
 * The codes arrive as one string rather than as an array so the effect can be
 * keyed on them: an array rebuilt every render would tear every channel down
 * and open it again on every render, which is the opposite of what a list of
 * rooms should cost.
 */
function useRoomCounts(codes: string): Record<string, number> {
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const list = codes.split(" ").filter(Boolean);
    if (list.length === 0) return;
    const stops = list.map((code) =>
      peekRoom(code, (peek: RoomPeek) => {
        // Only an answer is drawn. A room that has not replied yet, or did not
        // reply at all, says nothing rather than nought — "0/12" on a room that
        // is simply out of reach is the screen making something up.
        if (peek.kind !== "answered") return;
        setCounts((was) =>
          was[code] === peek.count ? was : { ...was, [code]: peek.count },
        );
      }),
    );
    return () => stops.forEach((stop) => stop());
  }, [codes]);

  return counts;
}
